import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Globe, Users, Trophy } from "lucide-react";

/*  SOCIETY WELCOME BANNER — one-shot callout for the first-visit arc.
    Appears AFTER the Transmission intro clears + the Interrogation modal
    has had its moment. Dismissible, 14-day cooldown via localStorage.

    Placement decision: homepage only. The point is to put a face on the
    community for brand-new visitors, not nag returning ones.
*/
const SEEN_KEY  = "wm_society_welcome_v1";
const INTRO_KEY = "wm_transmission_seen_v1";

const _recently = (key, hours = 14 * 24) => {
  try {
    const raw = parseInt(localStorage.getItem(key) || "0", 10);
    if (!raw) return false;
    return (Date.now() - raw) / 36e5 < hours;
  } catch { return false; }
};

export const SocietyWelcome = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname !== "/") return;
    if (_recently(SEEN_KEY)) return;

    let cancelled = false;
    const t0 = Date.now();
    const iv = setInterval(() => {
      if (cancelled) return;
      try {
        if (localStorage.getItem(INTRO_KEY) === "1") {
          clearInterval(iv);
          // Wait a hair after the Interrogation might have landed
          setTimeout(() => { if (!cancelled) setVisible(true); }, 9000);
        } else if (Date.now() - t0 > 20000) {
          clearInterval(iv);
          if (!cancelled) setVisible(true);
        }
      } catch {}
    }, 500);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 pointer-events-none"
      role="dialog"
      aria-label="Join the Detective Society"
      data-testid="society-welcome-banner"
    >
      <div className="pointer-events-auto max-w-md mx-auto card-noir border-[#d4af37]/50 shadow-2xl p-4 relative">
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-[#5a5050] hover:text-[#d4af37]"
          data-testid="society-welcome-dismiss"
        >
          <X size={14} />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[16px]">🕵️</span>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
            WELCOME TO THE DETECTIVE SOCIETY
          </div>
        </div>
        <div className="text-sm text-[#f4f0ec] mb-3 leading-snug">
          Three suspects. One liar. <span className="text-[#d4af37]">Who do you trust?</span>
        </div>
        <div className="text-[11px] text-[#a39797] mb-4 leading-relaxed">
          Meet detectives from around the world · test your instincts · bring your crew.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/society"
            onClick={dismiss}
            className="btn-primary text-xs"
            data-testid="society-welcome-cta"
          >
            Enter the Society
          </Link>
          <button
            onClick={dismiss}
            className="btn-ghost text-[10px]"
            data-testid="society-welcome-skip"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default SocietyWelcome;
